import * as locales from 'date-fns/locale';
import { format } from 'date-fns-tz';

import { isEqual, isSameDay, isSameMonth, isSameYear, isValid } from 'date-fns';
import { capitalize } from '@mui/material/utils';
import { i18n } from 'i18n';
import { getPaymentData } from './transform';

const currencies = {
  cad: { name: 'CAD', sign: '$' },
  usd: { name: '', sign: '$' },
  eur: { name: '', sign: '€' },
};

const formatPaymentDataByTypes = {
  card: ({ brand, last4 }) => `${capitalize(brand)} •••• ${last4}`,
  credit_card: ({ card_type, last_four_digits }) => `${capitalize(card_type)} •••• ${last_four_digits}`,
};

export const formatCurrencyName = (currency) => currencies[currency]?.name || '';

export const formatCurrencySign = (currency) => currencies[currency]?.sign || '';

// todo: move to i18n formatting
export const formatPriceValue = (price, decimal) =>
  decimal && !Number.isInteger(price)
    ? price.toLocaleString('en', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : price.toLocaleString('en');

// eslint-disable-next-line default-param-last
export const formatPrice = (price = null, currency, noZero, decimal) => {
  if (noZero && !price) return i18n?.t('common:general.free').toUpperCase() || String(0);
  const sign = formatCurrencySign(currency);
  const name = formatCurrencyName(currency);
  const priceValue = formatPriceValue(price, decimal);
  return `${sign}${priceValue} ${name}`.trimEnd();
};

// eslint-disable-next-line default-param-last
export const formatPriceDashboardCard = (price = 0, currency, noZero, decimal) => {
  if (noZero && price === 0) return i18n?.t('common:general.free').toUpperCase() || String(0);
  let name = formatCurrencyName(currency);
  if (!name) {
    name = formatCurrencySign(currency);
  } else {
    name = `${name} `;
  }
  const priceValue = formatPriceValue(price, decimal);
  return `${name}${priceValue}`.trimEnd();
};

export const formatDiscount = (type, value, currency) => {
  if (type === 'flat') return formatPrice(-value, currency, false, true);
  if (type === 'percent') return `${-value}%`;
};

// todo: move to i18n formatting
export const formatPriceRange = (minPrice, maxPrice, currency, noZero, decimal) => {
  if (minPrice === maxPrice) return formatPrice(minPrice, currency, noZero, decimal);
  if (noZero && minPrice === 0) {
    const divider = i18n?.t('common:general.to') || '-';
    const minPriceStr = formatPrice(minPrice, currency, noZero, decimal);
    const maxPriceStr = formatPrice(maxPrice, currency, noZero, decimal);
    return `${minPriceStr} ${divider} ${maxPriceStr}`;
  }
  const sign = formatCurrencySign(currency);
  const name = formatCurrencyName(currency);
  const minPriceValue = formatPriceValue(minPrice, decimal);
  const maxPriceValue = formatPriceValue(maxPrice, decimal);
  return `${sign}${minPriceValue} - ${sign}${maxPriceValue} ${name}`.trimEnd();
};

export const formatPriceStart = (minPrice, maxPrice, currency, noZero, decimal) => {
  const minPriceStr = formatPrice(minPrice, currency, noZero, decimal);
  if (minPrice === maxPrice) return minPriceStr;
  if (minPrice === 0 && maxPrice > 0) return `${i18n?.t('common:general.startingAt').toLowerCase()} ${minPriceStr}`;

  return `${i18n?.t('common:general.from').toLowerCase()} ${minPriceStr}`;
};

/**
 * DST-safe UTC → local Date converter using Intl API.
 * This ensures accurate conversions around DST transitions,
 * unlike date-fns-tz's utcToZonedTime, which may shift by 1h.
 */
export const getIntlDate = (date, timeZone) => {
  const d = typeof date === 'string' ? new Date(date) : date;
  // JPA-8249: Intl.DateTimeFormat#formatToParts throws "Invalid time value" for an invalid
  // Date (e.g. a malformed event end_at) instead of failing gracefully. Bail out early with
  // the invalid Date itself — formatDateFns below already treats it as "nothing to format".
  if (!isValid(d)) return d;
  const fmt = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

  const parts = fmt.formatToParts(d);
  const vals = Object.fromEntries(parts.map((p) => [p.type, p.value]));
  const iso = `${vals.year}-${vals.month}-${vals.day}T${vals.hour}:${vals.minute}:${vals.second}`;
  return new Date(iso);
};

/**
 * Base formatter with i18n locale support.
 *
 * JPA-8249: date-fns' format() throws "Invalid time value" instead of failing gracefully
 * when handed an invalid Date (e.g. an event with a malformed end_at). Since every date
 * formatter in this file routes through here, guarding once at this shared choke point
 * covers formatDate/formatDateTime/formatDuration/formatTimeDuration uniformly.
 */
export const formatDateFns = (date, pattern, options = {}) => {
  if (!isValid(date)) return '';
  return format(date, pattern, {
    ...options,
    locale: locales[i18n?.language],
  });
};

/**
 * Format date (DST-safe).
 */
export const formatDate = (date, pattern, timeZone = 'UTC') => {
  const zoned = getIntlDate(date, timeZone);
  return formatDateFns(zoned, pattern, { timeZone });
};

/**
 * Format date + time string (DST-safe).
 * Combines both date and time with optional toggles.
 */
export const formatDateTime = (date, timeZone = 'UTC', showDate = true, showTime = true) => {
  const zoned = getIntlDate(date, timeZone);
  const pattern = `${showDate ? 'iii MMM do' : ''}${showDate && showTime ? ' • ' : ''}${showTime ? 'p' : ''}`;
  return formatDateFns(zoned, pattern, { timeZone }).toLowerCase();
};

/**
 * Format duration range (startAt → endAt).
 * Handles same day, month, year variations for natural display.
 */
export const formatDuration = (startAt, endAt, timeZone = 'America/Detroit') => {
  if (!startAt) return '';

  const startAtDate = getIntlDate(startAt, timeZone);
  if (!endAt) return capitalize(formatDateFns(startAtDate, 'iii MMM d, yyyy', { timeZone }));

  const endAtDate = getIntlDate(endAt, timeZone);
  const isCurrentEndAtYear = isSameYear(startAtDate, endAtDate);

  if (isSameDay(startAtDate, endAtDate)) {
    return capitalize(formatDateFns(startAtDate, 'iii MMM d, yyyy', { timeZone }));
  }

  if (isSameMonth(startAtDate, endAtDate) && isCurrentEndAtYear) {
    return capitalize(
      `${formatDateFns(startAtDate, 'iii MMM d', { timeZone })}-${formatDateFns(endAtDate, 'd, yyyy', { timeZone })}`,
    );
  }

  if (!isSameMonth(startAtDate, endAtDate) && isCurrentEndAtYear) {
    return capitalize(
      `${formatDateFns(startAtDate, 'iii MMM d, yyyy', { timeZone })} - ${formatDateFns(endAtDate, 'iii MMM d, yyyy', {
        timeZone,
      })}`,
    );
  }

  if (!isSameYear(startAtDate, endAtDate)) {
    return capitalize(
      `${formatDateFns(startAtDate, 'iii MMM d, yyyy', { timeZone })} - ${formatDateFns(endAtDate, 'iii MMM d, yyyy', {
        timeZone,
      })}`,
    );
  }

  return capitalize(
    `${formatDateFns(startAtDate, 'iii MMM d, yyyy', { timeZone })} - ${formatDateFns(endAtDate, 'iii MMM d, yyyy', {
      timeZone,
    })}`,
  );
};

/**
 * Format time duration (start → end), optionally with time zone label.
 * Automatically handles DST transitions and 12/24h differences.
 */
export const formatTimeDuration = (dateLeft, dateRight, timeZone = 'America/Detroit', withTimeZone = true) => {
  const startAtDate = getIntlDate(dateLeft, timeZone);
  const startAtFormatted = formatDateFns(startAtDate, 'p');
  const timeZoneFormatted = formatDateFns(startAtDate, 'zzz', { timeZone });
  const startTimeDuration = withTimeZone ? `${startAtFormatted} | ${timeZoneFormatted}` : `${startAtFormatted}`;

  if (!dateRight) return startTimeDuration;

  const endAtDate = getIntlDate(dateRight, timeZone);

  if (isEqual(startAtDate, endAtDate)) return startTimeDuration;

  const endAtFormatted = formatDateFns(endAtDate, 'p');

  if (!withTimeZone) return `${startAtFormatted} - ${endAtFormatted}`;
  return `${startAtFormatted} - ${endAtFormatted} | ${timeZoneFormatted}`;
};

export const getTranslationName = (key, language) => `${key}_${language}`;

export const hasDefaultLanguageTranslation = (initialValues, defaultLanguage) =>
  Object.keys(initialValues).some((key) => {
    const language = key.substring(key.length, key.length - 2);
    return language === defaultLanguage && Boolean(initialValues[key]);
  });

export const formatPaymentMethod = (paymentMethod) => {
  const formatPaymentData = formatPaymentDataByTypes[paymentMethod.type];
  if (formatPaymentData) return formatPaymentData(getPaymentData(paymentMethod));
  return null;
};

export const formatBytes = (bytes, decimals = 2) => {
  if (bytes === 0) return '0 B';

  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return `${parseFloat((bytes / k ** i).toFixed(dm))} ${sizes[i]}`;
};

export const formatUserShort = (user) => {
  if (user?.first_name || user?.last_name) return [user.first_name, user.last_name].join(' ');
  if (user?.attendee_email) return user.attendee_email;
  return '';
};

export const numberWithCommas = (value) => {
  if (Number.isNaN(+value)) {
    return value;
  }
  const nf = new Intl.NumberFormat('en-US');
  return nf.format(value);
};

export const formatPhoneNumber = (phone) => {
  const cleaned = `${phone}`.replace(/\D/g, '');

  if (cleaned.length > 7) {
    return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }

  return phone;
};

export const formatDateRange = (startStr, endStr, timeZone = 'UTC') => {
  if (!startStr) return '';

  // Convert UTC → local Date in correct time zone (DST-safe)
  const start = getIntlDate(startStr, timeZone);

  const datePart = format(start, 'EEE, MMMM d'); // "Thu, April 16"
  const startTime = format(start, 'h:mmaaa').toLowerCase(); // "3:30pm"

  // JPA-8254: endStr is omitted when the session's end time is hidden — degrade to start-only,
  // same as formatTimeDuration, instead of dropping the whole range.
  if (!endStr) return `${datePart} ${startTime}`;

  const end = getIntlDate(endStr, timeZone);
  const endTime = format(end, 'h:mmaaa').toLowerCase(); // "5:30pm"

  return `${datePart} ${startTime} - ${endTime}`;
};
